'use client';

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { VisuallyHidden } from '../visually-hidden';
import { ProgressStepperRootContext } from './context/progress-stepper-root.context';
import { ProgressStepperStepContext } from './context/progress-stepper-step.context';
import { ProgressStepperIcon } from './progress-stepper-icon';
import { progressStepperDotRecipe } from './styles/progress-stepper-dot.styles';
import type { ProgressStepperDotPropsInterface } from './types/progress-stepper-dot.types';

/**
 * ProgressStepper.Dot
 *
 * @since 8.57.0
 */
export const ProgressStepperDot = forwardRef<HTMLSpanElement, ProgressStepperDotPropsInterface>(
  (props: ProgressStepperDotPropsInterface, forwardedRef) => {
    const { className, css: cssProp, ...rest } = props;

    const { alignLabels, orientation } = useContext(ProgressStepperRootContext);
    const { active, interactive, tone } = useContext(ProgressStepperStepContext);

    // Combining visual variants into a single property to avoid excessive compound variants
    const variant = active ? 'active' : tone || 'notStarted';

    const styles = css(
      progressStepperDotRecipe.raw({ alignLabel: alignLabels, interactive, orientation, variant }),
      cssProp,
    );

    return (
      <span
        className={cx('c11n', styles, className)}
        data-c11n-component="ProgressStepper.Dot"
        data-c11n-version={__C11N_VERSION__}
        ref={forwardedRef}
        {...rest}
      >
        {tone && tone !== 'none' ? <VisuallyHidden>{tone}</VisuallyHidden> : null}
        <ProgressStepperIcon />
      </span>
    );
  },
);

ProgressStepperDot.displayName = 'ProgressStepper.Dot';
